import { useLazyQuery } from "@apollo/client";
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import {
  Button,
  Image,
  Page,
  PartialPage,
  Text,
  VStack,
  View,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InteractionManager, ScrollView } from "react-native";
import { Spacings } from "react-native-ui-lib";
import {
  LoadingScreen,
  ProductErrorGuard,
  ProductPlaceholder,
} from "../../../../../../components";
import { keys } from "../../../../../../utilities/i18n";
import { PRODUCT_QUERY } from "../../../../../../utilities/product/constants";
import { useImagesSentSuccessProvider } from "../../../../../photo-image/components/ImagesSentSuccessProvider/hooks";
import { useSelectUploadImageProvider } from "../../../../../photo-image/components/SelectUploadImageProvider/hooks";
import { ScannedBarcodeFormatBanner } from "../../../../components";

const baseImageStyle = {
  width: "100%",
  height: "100%",
  resizeMode: "cover",
} as const;

const imageContainerStyle = {
  width: "100%",
  aspectRatio: 1, // Set the aspect ratio to maintain image proportions
  maxWidth: 200,
  maxHeight: 200,
};

export interface ProductScannedPartialScreenProps {}

export function ProductScannedPartialScreen(
  props: ProductScannedPartialScreenProps
) {
  const { colors, spacings } = useThemeProvider();
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { t } = useTranslation();

  const { updateHandlers: updateSelectUploadImageHandlers } =
    useSelectUploadImageProvider();
  const { updateHandlers: updateImagesSentSuccessHandlers } =
    useImagesSentSuccessProvider();

  const {
    params: { productId, format },
  } = route;

  const [
    productQuery,
    {
      data: productData,
      loading: productLoading,
      error: productError,
      refetch: productRefetch,
    },
  ] = useLazyQuery<{
    product: Product;
  }>(PRODUCT_QUERY);

  useEffect(() => {
    productQuery({
      variables: {
        id: productId,
      },
    });
  }, [productId]);

  const product = productData?.product;

  const imageMarkup = product?.image?.path ? (
    <Image
      resizeMode="cover"
      source={{ uri: product.image.path }}
      style={baseImageStyle}
    />
  ) : (
    <ProductPlaceholder />
  );

  function handleTakeRoamPhoto() {
    navigation.goBack();

    InteractionManager.runAfterInteractions(() => {
      navigation.navigate("PhotoImageRootScreen", {
        screen: "ProductImageDetailsRootScreen",
        params: {
          screen: "SelectUploadImageMethodScreen",
          productId,
          params: {
            productId,
            sku: product?.stockNumber,
          },
        },
      });
    });
  }

  function handleScanAgain() {
    navigation.goBack();
  }

  useFocusEffect(
    useCallback(() => {
      updateSelectUploadImageHandlers({
        onUpload: () => undefined,
      });

      updateImagesSentSuccessHandlers({
        onTakeMore: (nav) => nav.goBack(),
        onNewProduct: (nav) => nav.pop(2),
        onFinished: (nav) => nav.pop(3),
      });
    }, [updateSelectUploadImageHandlers, updateImagesSentSuccessHandlers])
  );

  const footer = product ? (
    <VStack spacing={spacings.s2}>
      <Button
        style={{
          display: "flex",
        }}
        label={t(keys.takePhoto)}
        onPress={handleTakeRoamPhoto}
        color={colors.textOnPrimary}
        backgroundColor={colors.primary}
      />
      <Button
        variant="outline"
        style={{
          display: "flex",
        }}
        label={t(keys.scanAgain)}
        onPress={handleScanAgain}
        color={colors.text}
        backgroundColor={colors.primary}
      />
    </VStack>
  ) : null;

  return (
    <PartialPage
      scrollable={false}
      size="compact"
      onClose={() => navigation.goBack()}
      contentContainerStyle={{ flex: 1 }}
    >
      <ProductErrorGuard
        error={productError ? productError.message : undefined}
      >
        <Page
          footerStyles={{ padding: 0, borderTopWidth: 0 }}
          scrollable
          padding={0}
          cover
          footer={footer}
        >
          {product && !productLoading ? (
            <View flex>
              <View
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <ScrollView style={{ width: "100%" }}>
                  <VStack spacing={Spacings.s5}>
                    <ScannedBarcodeFormatBanner format={format} />
                    <View
                      style={{
                        ...imageContainerStyle,
                        alignSelf: "center",
                        marginBottom: Spacings.s5,
                      }}
                    >
                      {imageMarkup}
                    </View>
                    <View>
                      <Text
                        variant="heading2"
                        color="subdued"
                        numberOfLines={1}
                        style={{
                          marginBottom: Spacings.s2,
                          textAlign: "center",
                        }}
                      >
                        {product.stockNumber}
                      </Text>
                      <Text
                        numberOfLines={2}
                        variant="title"
                        style={{ textAlign: "center" }}
                      >
                        {product.displayName || product.name}
                      </Text>
                    </View>
                    <View
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outline"
                        isAction
                        size="compact"
                        label="View images"
                        onPress={() =>
                          navigation.navigate("PhotoImageRootScreen", {
                            screen: "ProductImageDetailsScreen",
                            params: {
                              productId: product.id,
                            },
                          })
                        }
                      />
                    </View>
                  </VStack>
                </ScrollView>
              </View>
              <View
                style={{
                  display: "flex",

                  flexDirection: "column-reverse",
                  paddingTop: Spacings.s5,
                  width: "100%",
                }}
              ></View>
            </View>
          ) : (
            <LoadingScreen message="Loading product." />
          )}
        </Page>
      </ProductErrorGuard>
    </PartialPage>
  );
}
